import { Trans, useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import ModoSobDemandaImage from '../../../assets/img/ModoSobDemanda.png';
import ModoSobDemandaAvancado from '../../../assets/img/ModoSobDemandaAvancado.png';
import { colors } from '~/styles/colors';
import { getContainerModal } from '~/helpers/modalImage';
import { Image } from 'antd';
import { Input } from '~/components/Input';
import { Select } from '~/components/Select';
import { defaultActionModeOptions, defaultActionPostModeOptions, ltiModeEcoOptions } from '../constants';
import { InputCalculator } from '~/components/InputCalculator';
import {
  ContentInnerCollapsible, ContentOuterCollapsible, HeaderCollapsible, IconCollapsible, TitleCollapsible,
} from '../styles';
import { useEffect, useRef, useState } from 'react';
import { BaseComponentProps, ModoSobDemandaData } from '../types';

export type { ModoSobDemandaData };

const DEFAULT_SETPOINT_VALUES = [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];
const DRIVE_TEMPERATURE_RANGE_VALUES = Array.from({ length: 31 }, (_, i) => i);

type ModoSobDemandaProps = {
  onChange: (data: ModoSobDemandaData) => void;
  irCommands: BaseComponentProps['irCommands'];
  isMultipleDevProg?: boolean;
  initialData?: Partial<ModoSobDemandaData>;
};

export const ModoSobDemanda = ({
  onChange,
  irCommands,
  isMultipleDevProg,
  initialData,
}: ModoSobDemandaProps): JSX.Element => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    driveTemperature: initialData?.driveTemperature ?? '19',
    actionMode: initialData?.actionMode ?? null,
    setpoint: initialData?.setpoint ?? (irCommands[0]?.TEMPER?.toString() ?? ''),
    actionTime: initialData?.actionTime ?? '5',
    actionPostMode: initialData?.actionPostMode ?? null,
    actionPostSetpoint: initialData?.actionPostSetpoint ?? (irCommands[0]?.TEMPER?.toString() ?? ''),
    lti_cmd: initialData?.lti_cmd ?? 'NO_ACTION',
    lti_degree: initialData?.lti_degree ?? ((initialData?.lti_cmd === 'FAN' || initialData?.lti_cmd === 'OFF') ? (irCommands[0]?.TEMPER?.toString() ?? '') : ''),
    upperHysteresis: initialData?.upperHysteresis ?? '1.0',
    lowerHysteresis: initialData?.lowerHysteresis ?? '1.0',
    upperHysteresisError: null as string | null,
    lowerHysteresisError: null as string | null,
    actionTimeError: null as string | null,
  });

  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const innerRef = useRef<HTMLDivElement | null>(null);

  const baseSetpointValues = isMultipleDevProg || irCommands.length === 0
    ? DEFAULT_SETPOINT_VALUES
    : irCommands.map((command) => command.TEMPER);
  const hasSetpoint = (state.actionMode === 'COOL' || state.actionMode === 'ECO') && !!state.setpoint;
  const setpointNum = hasSetpoint ? Number(state.setpoint) : null;
  const ltiPossibleValues = setpointNum !== null
    ? baseSetpointValues.filter((v) => v <= setpointNum)
    : baseSetpointValues;

  function increaseDecrease(value: string) {
    const newSetpoint = Number(value);
    setState((prev) => ({
      ...prev,
      setpoint: value,
      ...(hasSetpoint && (prev.lti_cmd === 'FAN' || prev.lti_cmd === 'OFF') && Number(prev.lti_degree) > newSetpoint && { lti_degree: value }),
    }));
  }

  useEffect(() => {
    onChange({
      driveTemperature: state.driveTemperature,
      actionMode: state.actionMode,
      setpoint: state.setpoint,
      actionTime: state.actionTime,
      actionPostMode: state.actionPostMode,
      actionPostSetpoint: state.actionPostSetpoint,
      lti_cmd: state.lti_cmd,
      lti_degree: state.lti_degree,
      upperHysteresis: state.upperHysteresis,
      lowerHysteresis: state.lowerHysteresis,
    });
  }, [state.driveTemperature, state.actionMode, state.setpoint, state.actionTime, state.actionPostMode, state.actionPostSetpoint, state.lti_cmd, state.lti_degree, state.upperHysteresis, state.lowerHysteresis]);

  useEffect(() => {
    if (innerRef.current) {
      setHeight(open ? innerRef.current.scrollHeight : 0);
    }
  }, [open]);

  return (
    <Flex>
      <Flex flexDirection="column" style={{ gap: '16px' }}>
        <div style={{ fontSize: '12px', textAlign: 'justify' }}>
          <Trans i18nKey="detalhesNovoModoControle2" components={[<hr style={{ borderColor: colors.Grey200, opacity: 0.3 }} />]} />
        </div>
        <Image
          width={370}
          height={209}
          src={ModoSobDemandaImage}
          preview={{
            getContainer: getContainerModal('schedule-modal'),
          }}
        />
        <InputCalculator
          data-testid="modo-sob-demanda-drive-temperature-input"
          allowManualInput
          label={`${t('temperaturaDeAcionamento')}`}
          value={state.driveTemperature}
          style={{ height: '50px', width: '400px' }}
          possibleValues={DRIVE_TEMPERATURE_RANGE_VALUES}
          unity="°C"
          onIncreaseDecrease={(value: string) => setState((prev) => ({ ...prev, driveTemperature: value }))}
        />
        <Flex
          style={{
            justifyContent: 'space-between',
            width: '400px',
          }}
        >
          <Select
            data-testid="modo-sob-demanda-action-mode-select"
            options={defaultActionModeOptions}
            value={defaultActionModeOptions.find((opt) => opt.value === state.actionMode) ?? null}
            placeholder={t('modoDeAcao')}
            onSelect={(item) => {
              setState((prev) => ({ ...prev, actionMode: item.value }));
            }}
            style={{ border: '0.7px solid #818181', width: state.actionMode === 'COOL' || state.actionMode === 'ECO' ? '188px' : '400px', marginBottom: '16px' }}
            propLabel="label"
            notNull
          />
          {(state.actionMode === 'COOL' || state.actionMode === 'ECO') && (
          <InputCalculator
            data-testid="modo-sob-demanda-setpoint-input"
            label="Setpoint [°C]"
            value={state.setpoint ? state.setpoint.toString() : ''}
            style={{ height: '50px' }}
            possibleValues={isMultipleDevProg || irCommands.length === 0 ? DEFAULT_SETPOINT_VALUES : irCommands.map((command) => command.TEMPER)}
            unity="ºC"
            onIncreaseDecrease={increaseDecrease}
            isSetpoint
            disabled={!isMultipleDevProg && irCommands.length === 0}
          />
          )}
        </Flex>
        <HeaderCollapsible
          data-testid="modo-sob-demanda-advanced-toggle"
          onClick={() => setOpen((s) => !s)}
          aria-expanded={open}
          aria-controls="collapsible-advanced-content"
          style={{ padding: '0px' }}
        >
          <TitleCollapsible>{t('configuracoesAdicionais')}</TitleCollapsible>
          <IconCollapsible $open={open} aria-hidden>
            ▼
          </IconCollapsible>
        </HeaderCollapsible>
        <ContentOuterCollapsible id="collapsible-advanced-content" $height={height} $open={open} style={{ padding: '0px', gap: '16px' }}>
          <ContentInnerCollapsible ref={innerRef} style={{ padding: '0px', gap: '16px' }}>
            <Flex flexDirection="column" style={{ gap: '16px' }}>
              <div style={{ fontSize: '12px', textAlign: 'justify', marginBottom: '10px' }}>
                <Trans i18nKey="detalhesNovoModoControle2Avancado" components={[<hr style={{ borderColor: colors.Grey200, opacity: 0.3 }} />]} />
              </div>
              <Image
                width={370}
                height={209}
                src={ModoSobDemandaAvancado}
                preview={{
                  getContainer: getContainerModal('schedule-modal'),
                }}
              />
              <Input
                data-testid="modo-sob-demanda-action-time-input"
                label={t('tempoDeAcao')}
                value={state.actionTime}
                type="number"
                suffix="min"
                style={{ width: '400px' }}
                error={state.actionTimeError}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const num = parseInt(value, 10);
                  const invalid = value !== '' && (Number.isNaN(num) || num < 1 || num > 480);
                  setState((prev) => ({
                    ...prev,
                    actionTime: value,
                    actionTimeError: invalid ? t('valorEntre', { min: '1', max: '480' }) : null,
                  }));
                }}
              />
              <Flex
                style={{
                  justifyContent: 'space-between',
                  width: '400px',
                }}
              >
                <Select
                  data-testid="modo-sob-demanda-action-post-mode-select"
                  options={defaultActionPostModeOptions}
                  value={defaultActionPostModeOptions.find((opt) => opt.value === state.actionPostMode) ?? null}
                  placeholder={t('comportamentoAposAcao')}
                  onSelect={(item) => {
                    setState((prev) => ({ ...prev, actionPostMode: item.value }));
                  }}
                  style={{ border: '0.7px solid #818181', width: state.actionPostMode === 'COOL' ? '188px' : '400px' }}
                  propLabel="label"
                  notNull
                />
                {state.actionPostMode === 'COOL' && (
                  <InputCalculator
                    data-testid="modo-sob-demanda-action-post-setpoint-input"
                    label="Setpoint [°C]"
                    value={state.actionPostSetpoint ? state.actionPostSetpoint.toString() : ''}
                    style={{ height: '50px' }}
                    possibleValues={baseSetpointValues}
                    unity="ºC"
                    onIncreaseDecrease={(value: string) => setState((prev) => ({ ...prev, actionPostSetpoint: value }))}
                    isSetpoint
                    disabled={!isMultipleDevProg && irCommands.length === 0}
                  />
                )}
              </Flex>
              <Flex
                flexWrap="nowrap"
                flexDirection="row"
                style={{ width: '400px', justifyContent: 'space-between' }}
              >
                <Input
                  data-testid="modo-sob-demanda-upper-hysteresis-input"
                  label={t('histereseSuperiorC')}
                  value={state.upperHysteresis}
                  placeholder="0.0"
                  suffix="°C"
                  style={{ width: '188px' }}
                  error={state.upperHysteresisError}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                    const dotIndex = raw.indexOf('.');
                    const normalized = dotIndex === -1 ? raw : raw.slice(0, dotIndex + 1) + raw.slice(dotIndex + 1).replace(/\./g, '');
                    const parts = normalized.split('.');
                    const value = parts.length > 1 && parts[1].length > 1 ? parseFloat(normalized).toFixed(1) : normalized;
                    const num = parseFloat(value);
                    const invalid = value !== '' && (Number.isNaN(num) || num < 0 || num > 5);
                    setState((prev) => ({
                      ...prev,
                      upperHysteresis: value,
                      upperHysteresisError: invalid ? t('valorEntre', { min: '0.0', max: '5.0' }) : null,
                    }));
                  }}
                />
                <Input
                  data-testid="modo-sob-demanda-lower-hysteresis-input"
                  label={t('histereseInferiorC')}
                  value={state.lowerHysteresis}
                  placeholder="0.0"
                  suffix="°C"
                  style={{ width: '188px' }}
                  error={state.lowerHysteresisError}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                    const dotIndex = raw.indexOf('.');
                    const normalized = dotIndex === -1 ? raw : raw.slice(0, dotIndex + 1) + raw.slice(dotIndex + 1).replace(/\./g, '');
                    const parts = normalized.split('.');
                    const value = parts.length > 1 && parts[1].length > 1 ? parseFloat(normalized).toFixed(1) : normalized;
                    const num = parseFloat(value);
                    const invalid = value !== '' && (Number.isNaN(num) || num < 0 || num > 5);
                    setState((prev) => ({
                      ...prev,
                      lowerHysteresis: value,
                      lowerHysteresisError: invalid ? t('valorEntre', { min: '0.0', max: '5.0' }) : null,
                    }));
                  }}
                />
              </Flex>
              <Flex
                style={{
                  justifyContent: 'space-between',
                  width: '400px',
                }}
              >
                <Select
                  data-testid="modo-sob-demanda-lti-cmd-select"
                  options={ltiModeEcoOptions}
                  value={ltiModeEcoOptions.find((opt) => opt.value === state.lti_cmd) ?? null}
                  placeholder={t('acaoLTI')}
                  onSelect={(item) => {
                    setState((prev) => ({
                      ...prev,
                      lti_cmd: item.value,
                      ...((item.value === 'FAN' || item.value === 'OFF') && !prev.lti_degree && { lti_degree: irCommands[0]?.TEMPER?.toString() ?? '' }),
                    }));
                  }}
                  style={{ border: '0.7px solid #818181', width: (state.lti_cmd === 'FAN' || state.lti_cmd === 'OFF') ? '188px' : '400px', marginBottom: '16px' }}
                  propLabel="label"
                  notNull
                />
                {(state.lti_cmd === 'FAN' || state.lti_cmd === 'OFF') && (
                  <InputCalculator
                    data-testid="modo-sob-demanda-lti-degree-input"
                    allowManualInput
                    label={`${t('LTI')} [ºC]`}
                    value={state.lti_degree ? state.lti_degree.toString() : ''}
                    style={{ height: '50px' }}
                    possibleValues={ltiPossibleValues}
                    unity="ºC"
                    onIncreaseDecrease={(value: string) => setState((prev) => ({ ...prev, lti_degree: value }))}
                    isSetpoint
                    disabled={!isMultipleDevProg && irCommands.length === 0}
                    upperLimitMessage={setpointNum !== null ? t('limiteLTISetpoint', { setpoint: state.setpoint }) : undefined}
                  />
                )}
              </Flex>
            </Flex>
          </ContentInnerCollapsible>
        </ContentOuterCollapsible>
      </Flex>
    </Flex>
  );
};
